import { formatExtraLabel } from './formatExtraLabel';
import { formatTime } from './formatTime';

type SpaSelection = {
  name?: string | null;
  startTime?: string | null;
  extra?: { name?: string | null } | null;
};

type SpaSelectionGroup = {
  isExtras?: boolean | null;
  selections?: SpaSelection[] | null;
};

type WithSelectionGroups = { selectionGroups?: unknown };

type WithSpaExtras = WithSelectionGroups & { guestDetails?: unknown };

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const selectionsOf = (item: WithSelectionGroups, isExtras: boolean): SpaSelection[] =>
  asArray<SpaSelectionGroup | null>(item.selectionGroups)
    .filter((group): group is SpaSelectionGroup => !!group && !!group.isExtras === isExtras)
    .flatMap((group) => asArray<SpaSelection>(group.selections));

export const spaChoiceLines = (item: WithSelectionGroups): string[] => {
  const choices = new Map<string, { name: string; time?: string; count: number }>();

  selectionsOf(item, false).forEach((selection) => {
    if (!selection?.name) return;
    const time = selection.startTime ? formatTime(selection.startTime) : undefined;
    const key = `${time ?? ''}|${selection.name}`;
    const choice = choices.get(key) ?? { name: selection.name, time, count: 0 };
    choice.count += 1;
    choices.set(key, choice);
  });

  return [...choices.values()]
    .sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''))
    .map(({ name, time, count }) =>
      [formatExtraLabel(name, count), time].filter(Boolean).join(' · ')
    );
};

export const spaExtraLabels = (item: WithSpaExtras): string[] => {
  const guestExtras = asArray<{ extras?: unknown } | null>(item.guestDetails).flatMap((guest) =>
    asArray<SpaSelection>(guest?.extras)
  );

  const counts = [...guestExtras, ...selectionsOf(item, true)].reduce<Map<string, number>>(
    (acc, extra) => {
      const name = extra?.name ?? extra?.extra?.name;
      return name ? acc.set(name, (acc.get(name) ?? 0) + 1) : acc;
    },
    new Map()
  );

  return [...counts].map(([name, count]) => formatExtraLabel(name, count));
};

export const firstSpaChoiceTime = (item: WithSelectionGroups): string | undefined =>
  selectionsOf(item, false)
    .map((selection) => (selection?.startTime ? formatTime(selection.startTime) : undefined))
    .filter((time): time is string => Boolean(time))
    .sort()[0];
